import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { RecentReferralTable, InfoTile } from '..';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import {
  CFCProps,
  DashboardAssetOwnerStatsResponse,
  APP_URL,
  JOB_STATUS_CODE
} from 'types';
import { addAlert } from 'redux/alerts/actions';
import {
  setReferralPresetSearchValues,
  setForwardReferralIsListView
} from 'redux/referrals';
import Utils from 'utilities/helper.util';

const getEnquiryLodged = (rangeText: string | undefined, values: any) => {
  const rangeOptions = [
    { value: 'today', range: 'today' },
    { value: 'el-custom', range: 'this-week' },
    { value: '7', range: 'last-7-days' },
    { value: '30', range: 'last-30-days' }
  ];
  const result = rangeOptions.find((o) => rangeText === o.range);

  values.enquiryLodged = result?.value || '60';
  switch (rangeText) {
    case 'today': {
      const today = Utils.getNewDate().startOf('day');
      values.createdAfter = today;
      break;
    }
    case 'this-week': {
      const today = Utils.getNewDate().subtract(1, 'day');
      const startOfThisWeek = today.startOf('week').add(1, 'day');
      const endOfThisWeek = today.endOf('week').add(1, 'day');
      values.enquiryLodgedCustomed = true;
      values.createdAfter = startOfThisWeek;
      values.createdBefore = endOfThisWeek;
      break;
    }
    case 'last-7-days': {
      const date = Utils.getDateBeforeDays(6).startOf('day');
      values.createdAfter = date;
      break;
    }
    case 'last-30-days': {
      const date = Utils.getDateBeforeDays(29).startOf('day');
      values.createdAfter = date;
      break;
    }
  }

  return values;
};

export const getWaitTimeMin = (waittime?: number) => {
  return typeof waittime === 'number' ? (waittime / 60).toFixed(1) : '0';
};

export const AOInfos: React.FC<Props> = ({ rawAOStats, rangeText }) => {
  const [isLoadingStat, setIsLoadingStat] = useState<boolean>(true);
  const dispatch = useDispatch();
  const [aoStats, setAoStats] = useState<DashboardAssetOwnerStatsResponse>();
  const history = useHistory();

  const onClickReferralLink = useCallback(() => {
    const initialValues = {
      enquiryLodged: '60',
      jobStart: '',
      enquiryLodgedCustomed: false,
      jobStartCustomed: false,
      createdAfter: Utils.getDateBeforeDays(60).startOf('day'),
      createdBefore: undefined,
      digStartAfter: undefined,
      digStartBefore: undefined,
      status: [],
      requestorFullName: '',
      requestorEmail: ''
    };
    const updatedValues = getEnquiryLodged(rangeText, initialValues);
    dispatch(setReferralPresetSearchValues(updatedValues));
    dispatch(setForwardReferralIsListView(true));
    history.push(APP_URL.REFERRAL_HISTORY);
  }, [dispatch, history, rangeText]);

  const onClickWaitingReferralLink = useCallback(() => {
    const { WAITING, CREATED } = JOB_STATUS_CODE;
    const initialValues = {
      enquiryLodged: '60',
      jobStart: '',
      enquiryLodgedCustomed: false,
      jobStartCustomed: false,
      createdAfter: Utils.getDateBeforeDays(60).startOf('day'),
      createdBefore: undefined,
      digStartAfter: undefined,
      digStartBefore: undefined,
      status: [WAITING, CREATED],
      requestorFullName: '',
      requestorEmail: ''
    };
    const updatedValues = getEnquiryLodged(rangeText, initialValues);
    dispatch(setReferralPresetSearchValues(updatedValues));
    dispatch(setForwardReferralIsListView(true));
    history.push(APP_URL.REFERRAL_HISTORY);
  }, [dispatch, history, rangeText]);

  useEffect(() => {
    if (rawAOStats) {
      if (typeof rawAOStats === 'string') {
        dispatch(addAlert('dashbaord-alert', 'error', 'Error', rawAOStats));
      } else {
        setAoStats(rawAOStats);
        setIsLoadingStat(false);
      }
    } else {
      setAoStats(undefined);
      setIsLoadingStat(true);
    }
  }, [rawAOStats, dispatch]);

  return (
    <>
      <div className="dashboard-content-info-container">
        <InfoTile
          isLoading={isLoadingStat}
          title="Referrals"
          value={aoStats?.enquiryCount || 0}
          linkAction={() => onClickReferralLink()}
        />
        <InfoTile
          isLoading={isLoadingStat}
          invert={true}
          title="Outstanding responses"
          value={aoStats?.outstandingResponsesCount || 0}
          linkAction={() => onClickWaitingReferralLink()}
        />
      </div>
      <RecentReferralTable />
      <div className="dashboard-content-info-container">
        <InfoTile
          isLoading={isLoadingStat}
          title="Median response time"
          value={
            <>
              {getWaitTimeMin(aoStats?.typicalWaitTime?.details?.P50)}
              <span className="small-text"> min</span>
            </>
          }
        />
        <InfoTile
          isLoading={isLoadingStat}
          title="90% response times below"
          value={
            <>
              {getWaitTimeMin(aoStats?.typicalWaitTime?.details?.P90)}
              <span className="small-text"> min</span>
            </>
          }
        />
      </div>
      <div className="dashboard-content-info-container single">
        <InfoTile
          isLoading={isLoadingStat}
          title="Responder rank"
          value={
            <>
              {aoStats?.responseRank || 0}
              <span className="small-text">
                {' '}
                /{aoStats?.totalMemberCount || 0}
              </span>
            </>
          }
        />
      </div>
    </>
  );
};

const mapStateToProps = ({ dashboardCache }: RootState) => ({
  rawAOStats: dashboardCache.assetOwnerStats,
  rangeText: dashboardCache?.uiSelectionState?.rangeText
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(AOInfos));
